import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QuestionnaireField, formatIndianNumber, parseIndianNumber } from "@/config/questionnaireConfig";

interface QuestionnaireFormFieldProps {
  field: QuestionnaireField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
}

export function QuestionnaireFormField({ field, value, onChange, error }: QuestionnaireFormFieldProps) {
  const renderLabel = () => (
    <Label htmlFor={field.id} className="text-sm font-medium">
      {field.label}
      {field.validation?.required && <span className="text-destructive ml-1">*</span>}
    </Label>
  );

  const renderError = () => {
    if (!error) return null;
    return <p className="text-xs text-destructive mt-1">{error}</p>;
  };

  switch (field.type) {
    case 'currency':
      return (
        <div className="space-y-2">
          {renderLabel()}
          <Input
            id={field.id}
            type="text"
            placeholder={field.placeholder}
            value={value ? formatIndianNumber(value) : ''}
            onChange={(e) => {
              const rawValue = parseIndianNumber(e.target.value);
              onChange(rawValue);
            }}
            onWheel={(e) => e.currentTarget.blur()}
            className="h-12"
          />
          {renderError()}
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-2">
          {renderLabel()}
          <Textarea
            id={field.id}
            placeholder={field.placeholder}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="min-h-20 resize-none"
            rows={3}
          />
          {renderError()}
        </div>
      );

    case 'radio':
      return (
        <div className="space-y-3">
          {renderLabel()}
          <RadioGroup value={value || ''} onValueChange={onChange}>
            <div className="space-y-2">
              {field.options?.map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.value} id={`${field.id}-${option.value}`} />
                  <Label htmlFor={`${field.id}-${option.value}`} className="cursor-pointer font-normal">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
          </RadioGroup>
          {renderError()}
        </div>
      );

    case 'select':
      return (
        <div className="space-y-2">
          {renderLabel()}
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger className="h-12">
              <SelectValue placeholder={field.placeholder || 'Select an option'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {renderError()}
        </div>
      );

    case 'checkbox':
      return (
        <div className="space-y-3">
          {renderLabel()}
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.id}-${option.value}`}
                  checked={(value || []).includes(option.value)}
                  onCheckedChange={(checked) => {
                    const currentValues = value || [];
                    const newValues = checked
                      ? [...currentValues, option.value]
                      : currentValues.filter((v: string) => v !== option.value);
                    onChange(newValues);
                  }}
                />
                <Label htmlFor={`${field.id}-${option.value}`} className="cursor-pointer font-normal">
                  {option.label}
                </Label>
              </div>
            ))}
          </div>
          {renderError()}
        </div>
      );

    case 'date':
      return (
        <div className="space-y-2">
          {renderLabel()}
          <Input
            id={field.id}
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="h-12"
          />
          {renderError()}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-2">
          {renderLabel()}
          <Input
            id={field.id}
            type="number"
            placeholder={field.placeholder}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            className="h-12"
            min={field.validation?.min}
            max={field.validation?.max}
          />
          {renderError()}
        </div>
      );

    default: // text
      return (
        <div className="space-y-2">
          {renderLabel()}
          <Input
            id={field.id}
            type="text"
            placeholder={field.placeholder}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="h-12"
            maxLength={field.validation?.maxLength}
          />
          {renderError()}
        </div>
      );
  }
}
